// device-fingerprint — стабильный идентификатор устройства для device_sessions.
//
// Web:       SHA-256(UA + screen + timezone) → 64-char hex; persist в localStorage.
// Capacitor: Device.getId().identifier (нативный API, через `@studyhub/platform`).
//
// На web idempotency между сессиями достигается persist'ом fingerprint в localStorage
// — иначе при каждом cold start у пользователя плодились бы новые device_sessions.

const STORAGE_KEY = "studyhub.deviceId";

async function sha256Hex(input: string): Promise<string> {
	if (typeof window === "undefined") return input;
	const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
	return Array.from(new Uint8Array(buf))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
}

/** Получить (или сгенерировать и сохранить) device_id для web. */
export async function getOrCreateWebDeviceId(): Promise<string> {
	if (typeof window === "undefined") return "ssr";
	try {
		const saved = window.localStorage.getItem(STORAGE_KEY);
		if (saved) return saved;
	} catch {
		// ignore
	}
	const ua = window.navigator.userAgent;
	const lang = window.navigator.language;
	const screen = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
	const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
	const rand = window.crypto.getRandomValues(new Uint32Array(2)).join("-");
	const hash = await sha256Hex(`${ua}|${lang}|${screen}|${tz}|${rand}`);
	try {
		window.localStorage.setItem(STORAGE_KEY, hash);
	} catch {
		// ignore
	}
	return hash;
}

/** Человекочитаемое имя устройства — для UI «Мои устройства». */
export function getDeviceName(): string {
	if (typeof window === "undefined") return "Unknown";
	const ua = window.navigator.userAgent;
	// simple browser detection — для precision не критично
	if (/Edg/.test(ua)) return "Edge";
	if (/Chrome/.test(ua) && !/Chromium/.test(ua)) return "Chrome";
	if (/Firefox/.test(ua)) return "Firefox";
	if (/Safari/.test(ua) && !/Chrome/.test(ua)) return "Safari";
	return "Browser";
}
